import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageService } from '@core/services/storage.service';
import { environment } from '@env/environment';
import { Subscription, interval, throwError, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
@Injectable({
    providedIn: 'root',
})
export class LoginService {
    refreshSubscription?: Subscription;
    constructor(
        private readonly httpClient: HttpClient,
        private readonly localStorageService: StorageService,
    ) {}

    handleError(error: HttpErrorResponse) {
        let errorMessage;

        if (error.error instanceof ErrorEvent) {
            errorMessage = `LoginService.handleError()\nError: ${error.error.message}`;
        } else {
            errorMessage = `LoginService.handleError()\nError Code: ${error.status}\nMessage: ${error.message}`;
        }
        if (error.status === 403) {
            return throwError(() => new Error(error.error));
        }
        return throwError(() => new Error(errorMessage));
    }

    //debug point
    getAccessToken(code: string) {
        return this.httpClient
            .get(`${environment.commonApiUrl}login`, {
                params: { code },
            })
            .pipe(catchError(this.handleError));
    }

    getLoginRedirectUrl() {
        return this.httpClient
            .get(`${environment.commonApiUrl}login-redirect`)
            .pipe(catchError(this.handleError));
    }

    refreshAccessToken(refreshToken: string) {
        const accessToken = this.localStorageService.getItem(
            'fcsc-access-token'
        );
        return this.httpClient
            .post(`${environment.commonApiUrl}login-refresh`, {
                refresh_token: refreshToken,
            },{
                headers: {
                'Authorization': 'Bearer ' + accessToken,
                }
            })
            .pipe(catchError(this.handleError));
    }
    redirectToLogin(): void {
        // Capturing the vin and storing in local storage to use later for experttool
        const queryParams = new URLSearchParams(window.location.search);
        const vin = queryParams.get('vin');
        if (vin) {
        localStorage.setItem('vin', vin);
        }
        console.log('Redirecting to login...');
        window.location.href = `${environment.commonApiUrl}login-redirect`;
    }
    

    startRefreshAccessTokenInterval() {
        // Cancel previous scheduled refresh
        if (this.refreshSubscription) {
        this.refreshSubscription.unsubscribe();
        }
    
        const expiresAt = Number(this.localStorageService.getItem('fcsc-access-valid-till')); // full timestamp (ms)
        const refreshToken = this.localStorageService.getItem('fcsc-access-refresh-token');
    
        // Schedule refresh 3 minutes before expiry
        const refreshDelay = expiresAt - Date.now() - 3 * 60 * 1000;
        console.log(`Next token refresh scheduled in ${(refreshDelay / 60000).toFixed(1)} minutes.`);            
        
        // Schedule single refresh before expiry
        this.refreshSubscription = timer(refreshDelay).subscribe(() => {
        console.log('Refreshing access token now...');
    
        this.refreshAccessToken(refreshToken).subscribe({
            next: (response: any) => {
            if (response?.access_token && response?.expires_in) {
                console.log('Token refreshed successfully.');
                this.setResponseDataToLocalStorage(response);
                this.startRefreshAccessTokenInterval(); // restart with new expiry
            } else {
                console.log('Invalid refresh response — clearing local storage.');
                this.localStorageService.clearData();
                this.redirectToLogin();
            }
            },
            error: (error) => {
            console.log(`Error during token refresh: ${error}`);
            this.localStorageService.clearData();
            this.redirectToLogin();
            },
        });
        });
    }

    setResponseDataToLocalStorage(response: any) {
        if (response && response.access_token) {
            this.localStorageService.setItem(
                'fcsc-access-token',
                response.access_token
            );

            this.localStorageService.setItem(
                'fcsc-access-refresh-token',
                response.refresh_token
            );

            this.localStorageService.setItem(
                'fcsc-access-expires-in',
                response.expires_in
            );

            this.localStorageService.setItem(
                'fcsc-access-valid-till',
                new Date().getTime() + response.expires_in * 1000
            );
        }
    }
}